import compact from 'lodash/compact'
import filter from 'lodash/filter'
import find from 'lodash/find'
import flatten from 'lodash/flatten'
import indexOf from 'lodash/indexOf'
import isNil from 'lodash/isNil'
import map from 'lodash/map'
import omitBy from 'lodash/omitBy'
import reduce from 'lodash/reduce'
import sortBy from 'lodash/sortBy'
import uniq from 'lodash/uniq'
import uniqBy from 'lodash/uniqBy'

import { getColor } from '@Components/Chips/DiscountChip'
import { ProductPersonalization } from '@Components/Personalization/Modals/Shared'
import { ProductTileProps } from '@Domains/Product/Tiles/types'
import {
  Attribute,
  BaseAttribute,
  LinkedProductsFragment,
  Maybe,
  Product,
  ProductContentFragment,
  ProductVariant,
  ProductWithQuantity,
  Scalars,
} from '@Generated/graphql'
import { AlgoliaProductTile, AlgoliaProductV2 } from '@Globals/Algolia/Types'
import { giftWrappingIDs } from '@Shared/Statics/GiftWrappings'

export interface LinkedProduct {
  image: string
  productID: string
  title: string
  type: string
  url: string
}

const isBaseAttribute = (attribute: Attribute | undefined):
  attribute is BaseAttribute => attribute?.__typename === 'BaseAttribute'

export const getBaseAttribute = <T extends Scalars['JSON']>(attributes: Attribute[], key: string): Maybe<T> => {
  const attribute = attributes.find(x => x.key === key)
  return isBaseAttribute(attribute) ? attribute.value as T : null
}

export const getPersonalizationsFromProduct = ({ masterVariant }: Product): ProductPersonalization[] => (
  // @ts-ignore
  masterVariant?.personalizations?.products as ProductPersonalization[] || []
)

export const getPersonalizationTypes = (items: ProductPersonalization[]): string[] => compact(uniq(map(items, 'masterVariant.type.value.key')))

export const cmsProductToGridProduct = (
  product: Product,
  showBuyButton: boolean = false,
): ProductTileProps | undefined => {
  // TODO: Consider checking on more attributes
  // When no price is available for a product, there's no need to render it
  if (!product?.masterVariant?.price?.format) {
    return undefined
  }

  const normalPrice = product.masterVariant.price
  const price = product.masterVariant.price.discount ?? normalPrice
  // TODO: Get this from graph instead
  const discountPercentage = product.masterVariant.price.discount?.percent ?? 0
  // TODO: Refactor to use query-specific product data, to make primaryAsset available directly.
  // Currently we use type hints that does not match what we query, which hinders a lot of what graph gives us.
  // No custom resolvers on a product is available currently.
  const primaryAsset = find(product.assets, asset => indexOf(asset.tags || [], 'primary') !== -1) || product.assets?.[0]
  const primaryImage = primaryAsset?.url

  const isInStock = product.availability?.isInStock
  const quantity = product.availability?.quantity || 0
  const isOutOfStock = !isInStock && quantity <= 0

  const discountContext = product.masterVariant?.price?.discount?.code?.context

  return {
    beforePrice: (product.masterVariant.price.discount && normalPrice.format) || undefined,
    brandTitle: product.brand?.title || '',
    discount: (discountPercentage || discountContext)
      ? ({
        percentage: discountPercentage,
        ...discountContext && { context: discountContext },
      })
      : undefined,
    id: product.id,
    isOutOfStock,
    price: price.format || '',
    primaryImage,
    showBuyButton,
    title: product.name || '',
    url: product.url || '',
    // TODO: resolve variantsColorCount from the product query
    variantsColorCount: 0,
  }
}

export const buildLinkedProductList = (product?: Product): LinkedProduct[] => flatten(
  map(product?.linkedProducts as LinkedProductsFragment[], ({
    products,
    type,
  }) => map(
    // @ts-ignore - because restock.value doesn't exist in the types.
    filter(products, ({
      availability,
      masterVariant,
    }) => availability?.isInStock || (masterVariant?.restock as BaseAttribute)?.value !== 'disabled'),
    ({
      id = '',
      name = '',
      primaryAsset,
      url,
    }): LinkedProduct => ({
      productID: id || '',
      type: type || '',
      url: url || '',
      image: primaryAsset?.url || '',
      title: name || '',
    }),
  )),
)

export const getLinkedProductIDs = (product?: Product): string[] => uniq(flatten(
  map(product?.linkedProducts as LinkedProductsFragment[], ({ products }) => map(
    products,
    ({ id }) => (id),
  )),
))

// @ts-ignore - "product.bundle" doesn't exist in the auto-generated types, but it's in the query.
export const getBundledProductList = (product?: Product): ProductWithQuantity[] => (product?.masterVariant?.bundleContent?.products || [])

export const getGalleryAssetsFromProduct = (product?: Product): Product['assets'] => filter(
  product?.assets,
  asset => asset.tags?.includes('gallery'),
) as Product['assets']

export const getFirstVideoIndexInAssets = (assets?: Product['assets']) => assets?.findIndex(asset => asset.tags?.includes('video'))

export type CrossSellProduct =
  Pick<Product, 'name' | 'url' | 'id' | 'masterVariant'>
  & Pick<ProductContentFragment, 'primaryAsset'>

// @ts-ignore - ditto.
export const getCrossSellsForProduct = (product?: Product): CrossSellProduct[] => (product?.masterVariant?.crossSells?.products || [])

export function getTypeAndSubTypeForVariant(variant?: ProductVariant | null) {
  return {
    subType: getBaseAttribute<{ key?: string }>(variant?.attributes || [], 'subType')?.key || null,
    type: getBaseAttribute<{ key?: string }>(variant?.attributes || [], 'type')?.key || null,
  }
}

type GetAvailableVariantsProps = {
  includeVariantsThatWillNeverRestock?: boolean
  locale?: string
  product?: Product
}
export type SizeProps = {
  available?: boolean
  id: ProductVariant['id']
  key: string
  label: string
}

type FilteredVariantsReturnFormat = {
  availableVariants: SizeProps[]
  variantsInTotal: number
}

export const getAvailableVariantsForProduct = (
  {
    includeVariantsThatWillNeverRestock,
    locale,
    product,
  }: GetAvailableVariantsProps,
): FilteredVariantsReturnFormat => {
  if (!product) {
    return {
      availableVariants: [],
      variantsInTotal: 0,
    }
  }

  let variantsInTotal = 0
  const availableVariants = reduce<ProductVariant, SizeProps[]>(
    (product.variants || []) as ProductVariant[],
    (memo, item) => {
      const size: Record<string, any> | undefined = find(item.attributes, ['key', 'size'])
      if (!size) {
        return memo
      }

      const available = item.availability?.isInStock || false
      variantsInTotal += 1

      // If the product isn't available, we need to know if it ever will be again. If it isn't
      // due a restock at any time, there's no reason to show it to the user. They'll just be sad.
      // However, we might want to include the "dead" variants in certain cases, which is why we'll
      // do a double-check here.
      if (!available && !includeVariantsThatWillNeverRestock) {
        const restock: Record<string, any> | undefined = find(item.attributes, ['key', 'restock'])

        if (restock?.value?.key !== 'enabled') {
          return memo
        }
      }

      return [
        ...memo,
        {
          available,
          id: item.id,
          key: size?.value?.key || '',
          label: size?.value?.label?.[locale || 'en-GB'] || size?.value?.key || '',
        },
      ]
    },
    [],
  )

  return {
    availableVariants,
    variantsInTotal,
  }
}

const sortProductPersonalizations = (personalizations: ProductPersonalization[]) => {
  const preferredOrder = ['uv-print', 'engrave', 'deboss']
  return sortBy(personalizations, p => preferredOrder.indexOf(p.masterVariant.type.value.key))
}

export const getUniqProductPersonalizations = (product: Product): ProductPersonalization[] => {
  const personalizations = getPersonalizationsFromProduct(product)
  return sortProductPersonalizations(uniqBy(personalizations, p => p.masterVariant.type.value.key))
}
export const getProductGiftWrapping = (product: Product): CrossSellProduct | undefined => {
  const crossSellsForProduct = getCrossSellsForProduct(product)
  return crossSellsForProduct.find(cs => giftWrappingIDs.includes(cs.id))
}

export const getProductCrossSellGroups = (product: Product): {
  crossSells: CrossSellProduct[]
  giftWrappings: CrossSellProduct[]
  personalizations: ProductPersonalization[]
} => {
  const personalizations = getUniqProductPersonalizations(product)
  const giftWrapping = getProductGiftWrapping(product)
  const crossSells = getCrossSellsForProduct(product)
    .filter(cs => cs.id !== giftWrapping?.id)
  return {
    crossSells,
    giftWrappings: giftWrapping ? [giftWrapping] : [],
    personalizations,
  }
}

export const getPersonalization = (item: any) => ({
  id: item.id || '',
  price: item.masterVariant?.price?.format || '',
  discountedPrice: item.masterVariant?.price?.discount?.format || '',
  isFree: item.masterVariant?.price?.value === 0 || item.masterVariant?.price?.discount?.value === 0 || false,
  image: item.primaryAsset?.url || item.asset?.url || '',
  placement: item.masterVariant?.shortName?.value || {},
  maxCharacters: item.masterVariant?.maxCharacters?.value,
  type: item.masterVariant?.type?.value.key,
})

export const algoliaProductToProductTile = (product: AlgoliaProductV2): AlgoliaProductTile => omitBy<AlgoliaProductTile>({
  beforePrice: product.displayPrice?.discount ? product.displayPrice.format : undefined,
  bottomChips: compact([
    product.isNew && !product.isBestSeller && {
      color: 'blue',
      translation: ['common:BADGE_NEW_LABEL'],
    },
    product.isBestSeller && !product.isNew && {
      color: 'blue',
      translation: ['common:WB_BESTSELLERS_LABEL', { count: 1 }],
    },
    product.displayPrice.discount && {
      color: getColor(product.displayPrice.discount.code?.context || ''),
      translation: [
        'common:discount_chip',
        {
          context: product.displayPrice.discount.code?.context || '',
          discountPercentage: product.displayPrice?.discount?.percent || 0,
        },
      ],
    },
  ]),
  brandTitle: product.brand,
  bundleCombinedValue: product.bundle?.combinedValue.format,
  category: product.categoryPath,
  discount: product.displayPrice?.discount && {
    context: product.displayPrice.discount.code?.context || '',
    percentage: product.displayPrice?.discount?.percent || 0,
  },
  groupID: product.groupID || null,
  id: product.objectID,
  isOutOfStock: !product.isInStock,
  price: product.displayPrice?.discount ? product.displayPrice.discount.format : product.displayPrice?.format,
  priceValue: product.displayPrice?.discount ? product.displayPrice.discount.plain : product.displayPrice?.plain || 0,
  primaryImage: product.primaryAsset.url,
  priority: false,
  hoverImage: product.hoverAsset.url,
  sourceID: 'inherit',
  title: product.title,
  topChips: compact([
    product.personalizationTypes && product.personalizationTypes.includes('engrave') && {
      color: 'greyV2',
      translation: ['products:ENGRAVE_LABLE'],
    },
    product.personalizationTypes && !product.personalizationTypes.includes('engrave') && {
      color: 'greyV2',
      translation: ['common:PERSONALISABLE_PRODUCT_TAG'],
    },
    !!product?.isPopularGift && {
      color: 'blue',
      translation: ['common:POPULAR_GIFT_TAG'],
    },
  ]),
  url: product.slug,
  variantsColorCount: product?.variantsColorCount || 0,
}, isNil) as unknown as AlgoliaProductTile

export const isGiftWrapping = (product: any) => reduce(
  product?.masterVariant?.bundleContent?.products || [],
  (memo, productWithQuantity) => {
    if (memo) {
      return memo
    }

    return productWithQuantity?.product?.type === 'giftWrappings'
  },
  product?.type === 'giftWrappings',
)
